import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { scrollToId } from "@/utils/scroll";

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.12 },
});

const METRICS = [
  { v: "16K+", l: "Viste su Behance" },
  { v: "680+", l: "Apprezzamenti" },
  { v: "7+", l: "Anni di esperienza" },
  { v: "F&B", l: "Specializzazione" },
];

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain relative overflow-hidden pt-[72px]"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 pb-16 pt-14 sm:px-8 lg:grid-cols-12 lg:gap-6 lg:pb-24 lg:pt-20">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mb-8 font-mono text-[11px] tracking-[0.25em] text-neutral-500 sm:text-xs"
            data-testid="hero-eyebrow"
          >
            BEPPE FASINO — FOOD & BEVERAGE BRAND DESIGNER — BARI (IT)
          </motion.p>

          <h1 className="font-display text-[13.5vw] font-bold leading-[0.92] tracking-tighter sm:text-7xl lg:text-[5.6rem] xl:text-[6.4rem]">
            <span className="mask-line">
              <motion.span {...lineAnim(0)}>In cerca di</motion.span>
            </span>
            <span className="mask-line">
              <motion.span {...lineAnim(1)}>
                un'<span className="font-black italic text-cyan-brand">identità</span>
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span {...lineAnim(2)}>
                che <span className="font-black italic text-magenta-brand">resta</span>
                <span className="text-blue-brand">?</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg"
            data-testid="hero-subtitle"
          >
            Aiuto produttori, brand e aziende del settore Food & Beverage che
            hanno prodotti di valore ma un'identità visiva frammentata o poco
            riconoscibile a costruire brand distintivi, coerenti e capaci di
            valorizzare ciò che li rende unici.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-portfolio"
              onClick={() => scrollToId("portfolio")}
              className="group flex items-center gap-3 bg-ink px-7 py-4 font-mono text-xs font-medium tracking-wider text-paper transition-colors duration-300 hover:bg-magenta-brand"
            >
              ESPLORA IL PORTFOLIO
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToId("contatti")}
              className="group flex items-center gap-3 border border-ink px-7 py-4 font-mono text-xs font-medium tracking-wider transition-colors duration-300 hover:border-cyan-brand hover:bg-cyan-brand"
            >
              <MessageCircle className="h-4 w-4" />
              CONTATTAMI
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-16 grid grid-cols-2 gap-6 border-t border-neutral-200 pt-8 sm:grid-cols-4"
            data-testid="hero-metrics"
          >
            {METRICS.map((m) => (
              <div key={m.l}>
                <div className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  {m.v}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-neutral-500">
                  {m.l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.6 }}
            className="relative mx-auto aspect-square max-w-[460px]"
          >
            <motion.div style={{ y: yImg }} className="relative z-10 h-full w-full">
              <img
                src="/assets/portrait.png"
                alt="Beppe Fasino — Food & Beverage Brand Designer"
                data-testid="hero-portrait"
                className="h-full w-full object-cover"
              />
            </motion.div>
            <div className="absolute -bottom-5 left-6 z-20 bg-ink px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-paper">
              EST. 2017 — BARI, PUGLIA
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
